import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import Waveform from "@/components/Waveform";

export default function PaymentSuccess() {
  const { clear } = useCart();
  const { user } = useAuth();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const [sessionId] = useState(() => new URLSearchParams(window.location.search).get("session_id"));

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let timer;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid"); setOrder(data); clear();
          return;
        }
      } catch (e) {
        console.warn("Payment status check failed:", e.message);
      }
      if (cancelled) return;
      if (attempts < 8) timer = setTimeout(poll, 1800);
      else setStatus("pending");
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [sessionId, clear]);

  useEffect(() => {
    if (status === "paid" && user && sessionId) {
      api.post(`/orders/link/${sessionId}`).catch((e) => console.warn("Order link failed:", e.message));
    }
  }, [status, user, sessionId]);

  return (
    <div className="bg-ink min-h-screen flex items-center justify-center px-6 pt-24 pb-16" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg text-center">
        {status === "checking" && <p className="font-mono text-graphite uppercase tracking-widest">Confirming your order…</p>}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 bg-signal text-ink flex items-center justify-center mx-auto mb-8"><Check size={32} /></div>
            <p className="eyebrow mb-4">Order confirmed</p>
            <h1 className="font-anton uppercase text-5xl sm:text-6xl leading-none">You're in sync</h1>
            <Waveform color="rgb(var(--signal))" className="my-10" />
            <p className="text-chalk/80">Thanks for moving with us. A confirmation email is on its way.</p>
            {order?.amount != null && <p className="font-anton text-3xl mt-4">${order.amount.toFixed(2)}</p>}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
              <Link to="/account" className="btn-pulse">View Orders</Link>
              <Link to="/shop" className="btn-outline">Keep Shopping</Link>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <h1 className="font-anton uppercase text-4xl">Payment processing</h1>
            <p className="text-graphite mt-4">This is taking a moment. Check your account shortly for confirmation.</p>
            <Link to="/account" className="btn-outline mt-8">Go to Account</Link>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="font-anton uppercase text-4xl">Something went wrong</h1>
            <Link to="/shop" className="btn-pulse mt-8">Back to Shop</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
